// data/api/reportApi.js
import axiosInstance from "./mainApi";
import store from "../store/store";
import {
  setReportLoading,
  replaceAllTemplates,
  addReportTemplate,
  updateReportTemplate,
  deleteReportTemplate as deleteReportTemplateFromStore
} from "../store/actions/reportStoreFunctions";

/**
 * Backend -> UI param típus mappelés
 * Backend: DATE_RANGE | ENUM | TEXT | NUMBER | CHECKBOX  (vagy a te "1:Number" jellegű definíciódból bontva)
 * UI: dateRange | enum | text | number | checkbox
 */
// Backend -> UI param típus mappelés (robosztusabb, a range-eket előbb kezeli)
const toUiParamType = (t) => {
  if (!t) return "text";
  // normalizáljuk: "NumberRange" | "NUMBER_RANGE" | "number range" -> "NUMBERRANGE"
  const norm = String(t)
    .trim()
    .toUpperCase()
    .replace(/[^A-Z]/g, "");

  if (norm.includes("DATERANGE")) return "dateRange";
  if (norm.includes("NUMBERRANGE")) return "numberRange";

  if (norm === "DATE") return "date";
  if (norm.includes("ENUM")) return "enum";
  if (norm.includes("LIST") || norm.includes("MULTISELECT")) return "list";
  if (norm.includes("CHECKBOX") || norm === "BOOLEAN" || norm === "BOOL")
    return "checkbox";
  if (norm.includes("NUMBER") || norm.includes("NUMERIC")) return "number";
  if (norm.includes("TEXT") || norm.includes("STRING")) return "text";

  return "text";
};
const byReportIdAsc = (a, b) =>
  Number(a.reportId ?? a.id) - Number(b.reportId ?? b.id);
/**
 * A te Report modellje stringekben tárol mindent. Ezeket „szétszedjük” UI struktúrára.
 * Példa a Report mezőkből:
 *   parameterType:         "1:Number,2:Text,3:Enum"
 *   parameterName:         "1:Szín,2:Minimum érték,3:Státusz"
 *   parameterDefaultValue: "1:2.5|2:Y401|3:Any"
 *   enumListPairs:         "3:Mind-Any,Egy sem-None | 4:Érték-Az,Érték-Az"
 *   query:                 "SELECT ..."
 *   extensions:            "CSV;XLSX;PDF"
 */
function reportToUiTemplate(report, templateList = []) {
  // 1) formátumok
  const formats = (report.extensions || "")
    .split(/[;,]/)
    .map((s) => s.trim())
    .filter(Boolean);
  // ehhez a reporthoz tartozó sablon rekordok
  const templatesForThisReport = (templateList || []).filter(
    (t) => String(t.reportId) === String(report.reportId)
  );

  // tényleges sablonból jövő formátumok
  const templateFormats = templatesForThisReport
    .map((t) => (t.extension || "").toUpperCase())
    .filter(Boolean);
  // 2) param-defek összeépítése
  const typeMap = {}; // { idx -> "Number"/"Text"/"Enum"... }
  (report.parameterType || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const [idx, t] = pair.split(":").map((x) => x.trim());
      typeMap[idx] = t;
    });

  const nameMap = {};
  (report.parameterName || "")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const [idx, nm] = pair.split(":").map((x) => x.trim());
      nameMap[idx] = nm;
    });

  const defaultMap = {};
  (report.parameterDefaultValue || "")
    .split("|")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const [idx, val] = pair.split(":").map((x) => x.trim());
      defaultMap[idx] = val;
    });

  // enum opciók: "3:Mind-Any,Egy sem-None | 4:Érték-Az,Érték-Az"
  const enumMap = {};
  (report.enumListPairs || "")
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean)
    .forEach((grp) => {
      const [idx, rest] = grp.split(":").map((x) => x.trim());
      if (!idx || !rest) return;
      const options = rest
        .split(",")
        .map((o) => o.trim())
        .filter(Boolean)
        .map((one) => {
          // "Címke-ID" (dash lehet a labelben? ha igen, az első '-'ig bontunk)
          const dashPos = one.indexOf("-");
          if (dashPos === -1) {
            return { id: one, label: one };
          }
          return {
            label: one.substring(0, dashPos).trim(),
            id: one.substring(dashPos + 1).trim()
          };
        });
      enumMap[idx] = options;
    });

  const paramIndexes = Object.keys(typeMap).sort(
    (a, b) => Number(a) - Number(b)
  );

  const parameters = paramIndexes.map((idx) => {
    const rawType = typeMap[idx];
    const label = nameMap[idx] || `Param ${idx}`;
    const uiType = toUiParamType(rawType);
    const defVal = defaultMap[idx];

    const param = {
      key: `p${idx}`,
      type: uiType,
      label
    };

    if (uiType === "enum") {
      param.options = enumMap[idx] || [{ id: "any", label: "Mind" }];
      if (defVal != null) param.default = defVal;
    } else if (uiType === "list") {
      // default: vesszővel elválasztott értékek -> tömb
      param.options = enumMap[idx] || [];
      param.default = defVal ? String(defVal).split(",").filter(Boolean) : [];
    } else if (uiType === "number") {
      param.default =
        defVal != null && defVal !== "" ? Number(defVal) : undefined;
    } else if (uiType === "checkbox") {
      param.default = defVal != null && (defVal === "true" || defVal === "1");
    } else if (uiType === "dateRange" || uiType === "numberRange") {
    } else if (uiType === "date") {
      param.default = defVal ?? "";
    } else {
      // text stb.
      param.default = defVal ?? "";
    }
    return param;
  });

  // template id + name/description a reportból: adunk alapot
  return {
    id: String(report.reportId), // UI sablon ID
    name: report.name || `Report #${report.reportId}`,
    description: report.description || "",
    parameters,
    formats,
    templateFormats,
    // + ami még kellhet a későbbiekben:
    _rawReport: report,
    // csatolt sablonfájlok (ReportTemplate) per extension:
    _templates: templatesForThisReport
  };
}

/**
 * BE -> UI teljes lista (reports + reportTemplates)
 */
const getAllReportTemplatesApi = async () => {
  try {
    store.dispatch(setReportLoading(true));
    const [reportsRes, templatesRes] = await Promise.all([
      axiosInstance.get("/reports"), // List<Report>
      axiosInstance.get("/report-templates") // List<ReportTemplate meta (fájlnév, contentType, extension, ...)>
    ]);
    const reports = reportsRes.data || [];
    const rts = templatesRes.data || [];
    console.log("Received reports:", reports);
    console.log("Received templates:", rts);
    const uiTemplates = reports
      .map((r) => reportToUiTemplate(r, rts))
      .slice()
      .sort(byReportIdAsc);
    console.log("Converted UI templates:", uiTemplates);
    store.dispatch(replaceAllTemplates(uiTemplates));
    return uiTemplates;
  } catch (e) {
    console.error("Hiba a sablonok betöltésekor:", e);
    throw e;
  } finally {
    store.dispatch(setReportLoading(false));
  }
};

/**
 * Új report meta + sablonfájl feltöltése (opcionális).
 * payload: {
 *   report: { ...a Report szerinti mezők... },
 *   templateFile?: File,
 *   templateExtension?: 'XLSX' | 'PDF' | 'CSV' | ...
 * }
 */
const createReportTemplateApi = (payload) => {
  return async (dispatch) => {
    try {
      // 1) Report létrehozás
      const repRes = await axiosInstance.post("/reports", payload.report);
      const createdReport = repRes.data;

      // 2) Template fájl (ha van)
      if (payload.templateFile && payload.templateExtension) {
        const form = new FormData();
        form.append("reportId", createdReport.reportId);
        form.append("extension", payload.templateExtension);
        form.append("file", payload.templateFile);
        await axiosInstance.post("/report-templates", form, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      // 3) Frissítsük a kliens oldali sablonlistát
      const [reportsRes, templatesRes] = await Promise.all([
        axiosInstance.get("/reports"),
        axiosInstance.get("/report-templates")
      ]);
      const uiTemplates = (reportsRes.data || [])
        .slice()
        .sort(byReportIdAsc)
        .map((r) => reportToUiTemplate(r, templatesRes.data || []))
        .slice()
        .sort(byReportIdAsc);

      dispatch(replaceAllTemplates(uiTemplates));
      return createdReport;
    } catch (e) {
      console.error("Hiba az új report/sablon létrehozásakor:", e);
      throw e;
    }
  };
};

const updateReportTemplateApi = (templateId, payload) => {
  // templateId = UI oldali id == reportId
  return async (dispatch) => {
    try {
      // 1) Report frissítés
      await axiosInstance.put(`/reports/${templateId}`, payload.report);

      // 2) (Opcionális) új sablonfájl feltöltése/megcserélése
      if (payload.templateFile && payload.templateExtension) {
        const form = new FormData();
        form.append("reportId", templateId);
        form.append("extension", payload.templateExtension);
        form.append("file", payload.templateFile);
        await axiosInstance.post(`/report-templates`, form, {
          headers: { "Content-Type": "multipart/form-data" }
        });
      }

      // 3) Lista újratöltése
      const [reportsRes, templatesRes] = await Promise.all([
        axiosInstance.get("/reports"),
        axiosInstance.get("/report-templates")
      ]);
      const uiTemplates = (reportsRes.data || [])
        .slice()
        .sort(byReportIdAsc)
        .map((r) => reportToUiTemplate(r, templatesRes.data || []))
        .slice()
        .sort(byReportIdAsc);
      dispatch(replaceAllTemplates(uiTemplates));
    } catch (e) {
      console.error("Hiba report/sablon frissítéskor:", e);
      throw e;
    }
  };
};

const deleteReportTemplateApi = (templateId) => {
  return async (dispatch) => {
    try {
      await axiosInstance.delete(`/reports/${templateId}`);
      // sablonfájlok is törölhetők (backenden gondoskodunk róla, vagy külön endpointtal)
      dispatch(deleteReportTemplateFromStore(templateId));
    } catch (e) {
      console.error("Hiba report törlésekor:", e);
      throw e;
    }
  };
};
const deleteReportTemplateFileApi = (templateFileId) => {
  return async (dispatch) => {
    await axiosInstance.delete(`/report-templates/${templateFileId}`);
    const [reportsRes, templatesRes] = await Promise.all([
      axiosInstance.get("/reports"),
      axiosInstance.get("/report-templates")
    ]);
    const uiTemplates = (reportsRes.data || [])
      .slice()
      .sort(byReportIdAsc)
      .map((r) => reportToUiTemplate(r, templatesRes.data || []))
      .slice()
      .sort(byReportIdAsc);

    dispatch(replaceAllTemplates(uiTemplates));
  };
};

const downloadReportTemplateFileApi = async (templateFileId) => {
  const res = await axiosInstance.get(
    `/report-templates/${templateFileId}/download`,
    { responseType: "blob" }
  );
  const dispo = res.headers["content-disposition"] || "";
  const m = dispo.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  const filename = m ? decodeURIComponent(m[1]) : `template_${templateFileId}`;
  return { blob: res.data, filename };
};

/**
 * Report futtatás – BINÁRIS letöltés
 * A te ReportsPage ezt hívja: runReportApi(templateId, format, params)
 */
const runReportApi = async (reportId, format, params) => {
  const extFromFormat = (f) => {
    const x = String(f || "").toLowerCase();
    if (x === "xlsx") return "xlsx";
    if (x === "csv") return "csv";
    if (x === "pdf") return "pdf";
    if (x === "html") return "html";
    if (x === "xml") return "xml";
    if (x === "json") return "json";
    return x || "dat";
  };
  const sanitizeFilename = (s) =>
    String(s || "")
      .replace(/[\/\\?%*:|"<>]/g, "_") // tiltott karakterek
      .replace(/\s+/g, " ") // több space -> 1 space
      .trim();
  const timestamp = () => {
    const d = new Date();
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}_${p(
      d.getHours()
    )}-${p(d.getMinutes())}-${p(d.getSeconds())}`;
  };
  const getReportNameFromStore = (id) => {
    const st = store.getState();
    const tpl = (st.reports?.templates || []).find(
      (t) => String(t.id) === String(id)
    );
    return tpl?.name || `Riport_${id}`;
  };
  const response = await axiosInstance.post(
    `/reports/${reportId}/run`,
    { format, parameters: params },
    { responseType: "blob" }
  );
  // 1) megpróbáljuk kiolvasni a backend által küldött fájlnevet (ha van)
  const disposition = response.headers["content-disposition"] || "";
  const cdMatch = disposition.match(
    /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i
  );
  const headerFilename = cdMatch ? decodeURIComponent(cdMatch[1]) : "";
  // 2) előnyben részesítjük a saját sémánkat: <Report név> <YYYY-MM-DD_HH-mm-ss>.<ext>
  const baseName = `${getReportNameFromStore(reportId)} ${timestamp()}`;
  const finalName = `${sanitizeFilename(baseName)}.${extFromFormat(format)}`;
  // 3) ha nagyon szeretnéd a headerből az EXT-et megtartani, itt kiolvashatod,
  //    de a kérés szerint a név mindenképp a report neve + idő legyen.
  return { blob: response.data, filename: finalName };
};

export default {
  getAllReportTemplatesApi,
  createReportTemplateApi,
  updateReportTemplateApi,
  deleteReportTemplateApi,
  deleteReportTemplateFileApi, // ⬅️ add
  downloadReportTemplateFileApi,
  runReportApi
};
